import { Spinner, SpinnerSize } from 'office-ui-fabric-react';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SORTING_KEYS } from '../../../constants/sortingKeys';
import { getAllHearingsInitialize } from '../../../features/actions/api';
import { isFetchingHearingsSelector } from '../../../features/apiFeatures/hearingSlice';
import {
  allHearingTypesSelector,
  isFetchingHearingTypesSelector,
} from '../../../features/apiFeatures/hearingTypeSlice';
import {
  allKleHierarchiesByHearingTypeIdSelector,
  allKleHierarchiesSelector,
  isFetchingKleHierarchiesSelector,
} from '../../../features/apiFeatures/kleHierarchySlice';
import { getEntityId } from '../../../features/helpers/apiHelpers';
import {
  getInitialDataKlePage,
  isUpdatingKleMappingsSelector,
  updateKleMappings,
} from '../../../features/pageFeatures/kleMappingAdministrationPageSlice';
import { comparators } from '../../../utilities/comparators';
import {
  filterAndSortingKeys,
  noSelectionKey,
  showAllKleHierarchies,
} from '../../../utilities/filterAndSortingsUtil';
import { HeaderWithCommandBar } from './HeaderWithCommandBar';
import { FilterDropdownsAndButtons } from './kleMappingAdministrationPage/FilterDropdownsAndButtons';
import { HearingTypeDropdownAndTooltip } from './kleMappingAdministrationPage/HearingTypeDropdownAndTooltip';
import {
  filterKleHierarchyItems,
  filterKleHierarchyMainGroupNumbers,
  filterKleHierarchySubGroupNumbers,
} from './kleMappingAdministrationPage/kleMappingFilters';
import { KleMappingList } from './kleMappingAdministrationPage/KleMappingList';
import { mapKleHierarchyItems } from './kleMappingAdministrationPage/mapKleHieararchyItems';

const KleMappingAdministrationPage = () => {
  const dispatch = useDispatch();
  const { t: translator } = useTranslation();

  useEffect(() => {
    dispatch(getInitialDataKlePage());
    return function cleanup() {
      dispatch(getAllHearingsInitialize());
    };
  }, [dispatch]);

  const isFetchingHearingTypes = useSelector(isFetchingHearingTypesSelector);
  const isFetchingKleHierarchies = useSelector(
    isFetchingKleHierarchiesSelector,
  );
  const isFetchingHearings = useSelector(isFetchingHearingsSelector);
  const isUpdatingKleMappings = useSelector(isUpdatingKleMappingsSelector);

  const [selectionCount, setSelectionCount] = useState(0);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [titleFilter, setTitleFilter] = useState(null);
  const [selectedHearingTypeKey, setSelectedHearingTypeKey] = useState(
    noSelectionKey,
  );
  const [selectedMainGroupKey, setSelectedMainGroupKey] = useState(
    noSelectionKey,
  );
  const [selectedSubGroupKey, setSelectedSubGroupKey] = useState(
    noSelectionKey,
  );
  const [filterAndSortingSelection, setFilterAndSortingSelection] = useState({
    [filterAndSortingKeys.title]: noSelectionKey,
    [filterAndSortingKeys.mainGroupNumber]: noSelectionKey,
    [filterAndSortingKeys.subGroupNumber]: noSelectionKey,
    [filterAndSortingKeys.isChosen]: showAllKleHierarchies,
  });

  const hearingTypes = useSelector(allHearingTypesSelector);
  const kleHierarchies = useSelector(allKleHierarchiesSelector);
  const hearingTypeKleHierarchiesDict = useSelector(
    allKleHierarchiesByHearingTypeIdSelector,
  );

  // This variable is to force the list to rerender by instantly spawning and despawning a Spinner.
  //This is to reset the HTML part of the selection of the list.
  const [shouldRerender, setShouldRerender] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);

  const itemsToRender = useMemo(
    () => kleHierarchies.map(mapKleHierarchyItems),
    [kleHierarchies],
  );
  const selectedHearingTypeKleHierarchies = useMemo(
    () => hearingTypeKleHierarchiesDict[selectedHearingTypeKey],
    [selectedHearingTypeKey, hearingTypeKleHierarchiesDict],
  );
  const sortedKleHierarchies = useMemo(
    () =>
      itemsToRender.sort(comparators.kleHierarchies[SORTING_KEYS.KLE_NUMBER]),
    [itemsToRender],
  );

  const filteredKleHierarchies = useMemo(
    () =>
      sortedKleHierarchies.filter(
        filterKleHierarchyItems(
          filterAndSortingSelection[filterAndSortingKeys.title],
          filterAndSortingSelection[filterAndSortingKeys.mainGroupNumber],
          filterAndSortingSelection[filterAndSortingKeys.subGroupNumber],
          filterAndSortingSelection[filterAndSortingKeys.isChosen],
          selectedItems,
        ),
      ),
    [filterAndSortingSelection, sortedKleHierarchies, selectedItems],
  );

  const mainGoupKleHierarchyOptions = sortedKleHierarchies.filter(
    filterKleHierarchyMainGroupNumbers,
  );

  const subGoupKleHierarchyOptions = sortedKleHierarchies.filter(
    filterKleHierarchySubGroupNumbers(
      filterAndSortingSelection[filterAndSortingKeys.mainGroupNumber],
    ),
  );

  const onSearchBarClick = () => {
    setShowSearchBar(!showSearchBar);
    if (showSearchBar) {
      onSetTitleFilter(null);
    }
  };

  const onChangeHearingTypeDropdown = (_, value) => {
    setSelectedHearingTypeKey(value.key);
    setSelectedMainGroupKey(noSelectionKey);
    setSelectedSubGroupKey(noSelectionKey);
    updateFilterAndSortingSelection({
      [filterAndSortingKeys.hearingType]: value.key,
      [filterAndSortingKeys.subGroupNumber]: noSelectionKey,
      [filterAndSortingKeys.mainGroupNumber]: noSelectionKey,
      [filterAndSortingKeys.isChosen]: showAllKleHierarchies,
    });
    setShouldRerender(true);
  };

  const onChangeKleMainGroupDropdown = (_, value) => {
    setSelectedMainGroupKey(value.key);
    const filterValue =
      value.key === noSelectionKey ? noSelectionKey : value.text;

    setSelectedSubGroupKey(noSelectionKey);

    updateFilterAndSortingSelection({
      [filterAndSortingKeys.subGroupNumber]: noSelectionKey,
      [filterAndSortingKeys.mainGroupNumber]: filterValue,
    });
  };

  const onChangeKleSubGroupDropdown = (_, value) => {
    setSelectedSubGroupKey(value.key);
    const filterValue =
      value.key === noSelectionKey ? noSelectionKey : value.text;

    updateFilterAndSortingSelection({
      [filterAndSortingKeys.subGroupNumber]: filterValue,
    });
  };

  const onChangeIsChosenDropdown = (_, value) => {
    updateFilterAndSortingSelection({
      [filterAndSortingKeys.isChosen]: value.key,
    });
  };

  const onSetTitleFilter = (value) => {
    setTitleFilter(value);
    updateFilterAndSortingSelection({
      [filterAndSortingKeys.title]: value || noSelectionKey,
    });
  };

  const updateFilterAndSortingSelection = (diff) => {
    let newState = Object.assign({}, filterAndSortingSelection, diff);
    setFilterAndSortingSelection(newState);
  };

  const resetSelected = useCallback(() => {
    const preMapped = selectedHearingTypeKleHierarchies?.map(getEntityId) || [];
    setSelectedItems(preMapped);
    setSelectionCount(preMapped.length);
  }, [selectedHearingTypeKleHierarchies]);

  const onChange = useCallback(
    (selected) => {
      setSelectedItems(selected);
      setSelectionCount(selected.length);
    },
    [setSelectedItems, setSelectionCount],
  );

  const onCancelButtonClick = useCallback(() => {
    resetSelected();
  }, [resetSelected]);

  const onSaveButtonClick = () => {
    selectedHearingTypeKey &&
      dispatch(
        updateKleMappings({
          hearingTypeId: selectedHearingTypeKey,
          kleHierarchyIds: selectedItems,
        }),
      );
  };

  useEffect(() => {
    setShouldRerender(false);
  }, [shouldRerender]);

  useEffect(() => {
    resetSelected();
  }, [selectedHearingTypeKey, resetSelected]);

  const isFetchingData =
    isFetchingKleHierarchies || isFetchingHearingTypes || isFetchingHearings;

  const showSpinner = isUpdatingKleMappings || shouldRerender;

  const disableDropdownAndButton =
    isUpdatingKleMappings || selectedHearingTypeKey === noSelectionKey;

  return (
    <div className="admin-page">
      <div className="admin-page-container">
        <HeaderWithCommandBar
          header={translator('pages.adminKleMappings.header')}
          titleFilterText={translator('pages.adminKleMappings.filterText')}
          showTitleFilter={showSearchBar}
          setShowTitleFilter={onSearchBarClick}
          titleFilter={titleFilter}
          setTitleFilter={onSetTitleFilter}
          disableGrid={true}
        />
        {isFetchingData ? (
          <Spinner size={SpinnerSize.large} />
        ) : (
          <div>
            <HearingTypeDropdownAndTooltip
              hearingTypes={hearingTypes}
              onChangeDropdown={onChangeHearingTypeDropdown}
              selectedHearingTypeKey={selectedHearingTypeKey}
              selectionCount={selectionCount}
            />
            <FilterDropdownsAndButtons
              mainGoupKleHierarchyOptions={mainGoupKleHierarchyOptions}
              subGoupKleHierarchyOptions={subGoupKleHierarchyOptions}
              selectedMainGroupKey={selectedMainGroupKey}
              selectedSubGroupKey={selectedSubGroupKey}
              onChangeKleMainGroupDropdown={onChangeKleMainGroupDropdown}
              onChangeKleSubGroupDropdown={onChangeKleSubGroupDropdown}
              onChangeIsChosenDropdown={onChangeIsChosenDropdown}
              onCancelButtonClick={onCancelButtonClick}
              onSaveButtonClick={onSaveButtonClick}
              disableDropdownAndButton={disableDropdownAndButton}
              selectedIsChosenKey={
                filterAndSortingSelection[filterAndSortingKeys.isChosen]
              }
            />
          </div>
        )}
        <KleMappingList
          items={filteredKleHierarchies}
          selectedHearingTypeKey={selectedHearingTypeKey}
          showSpinner={showSpinner}
          onChange={(newSelectedItems) => onChange(newSelectedItems)}
          selectedItems={selectedItems}
        />
      </div>
    </div>
  );
};
export { KleMappingAdministrationPage };
